import { decimalSum, type DecimalValue } from "@shared/values/decimal";
import type { BadgeVariant } from "@/components/ui/badge";
import { TRANSACTION_TYPE_DISPLAY } from "@/lib/transaction-display";
import { FIELD_LABELS, ORDERS_DIALOG_STACK_COPY, ORDER_DISPLAY_COPY } from "@/lib/ui-labels";
import { easternCalendarDay, formatDateTimeEastern } from "@/lib/utils";
import { ORDER_DIRECTION_VALUES, ORDER_STATUS_LABELS, ORDER_STATUS_VALUES, ORDER_STATUSES, type OrderDirection, type OrderStatus, ORDER_ACTIONS } from "@shared/orders/order-types";
import { ORDER_DOCUMENT_TYPE_LABELS, ORDER_DOCUMENT_TYPES, type OrderDocumentType } from "@shared/orders/order-documents";
import { orderBalance, orderValue } from "@shared/orders/order-money";
import type { DeliveryEntry } from "@shared/orders/order-delivery-schedule";
import { ORDER_HISTORY_EVENTS, isOrderHistoryEventType, type OrderHistoryEventType } from "@shared/orders/order-history-events";
import { FULFILLED_BALANCE_DUE_LABEL, isFulfilledWithBalanceDue } from "@shared/orders/order-views";

export { ORDER_DOCUMENT_TYPE_LABELS, ORDER_DOCUMENT_TYPES, type OrderDocumentType };

export { ORDER_DIRECTION_VALUES, ORDER_STATUS_LABELS, ORDER_STATUS_VALUES, type OrderDirection, type OrderStatus };

type DirectionDisplay = {
  label: string;
  pluralLabel: string;
  variant: BadgeVariant;
  status: Pick<Record<OrderStatus, string>, typeof ORDER_STATUSES.requested | typeof ORDER_STATUSES.ordered>;
  dates: { submittedAt: string; orderedAt: string; arrivedDate: string };
  requestor: string;
  placeAction: string;
  fulfillAction: string;
  movement: {
    plural: string; singular: string; past: string;
    menu: string; total: string; externalBatchQualifier: string;
  };
};

export const ORDER_DIRECTION_DISPLAY = {
  purchase: {
    label: "Purchase",
    pluralLabel: "Purchases",
    variant: TRANSACTION_TYPE_DISPLAY.PURCHASE.variant,
    status: { requested: ORDER_DISPLAY_COPY.purchaseRequested, ordered: ORDER_DISPLAY_COPY.orderPlaced },
    dates: { submittedAt: ORDER_DISPLAY_COPY.requestedOn, orderedAt: ORDER_DISPLAY_COPY.orderPlacedOn, arrivedDate: ORDER_DISPLAY_COPY.dateReceived },
    requestor: FIELD_LABELS.requestedBy,
    placeAction: ORDER_DISPLAY_COPY.placeOrder,
    fulfillAction: ORDER_DISPLAY_COPY.markFulfilledArrived,
    movement: {
      plural: "Receipts", singular: "Receipt", past: "Received",
      menu: ORDER_DISPLAY_COPY.incomingShipments, total: ORDER_DISPLAY_COPY.totalReceived, externalBatchQualifier: "Vendor",
    },
  },
  sale: {
    label: "Sale",
    pluralLabel: "Sales",
    variant: TRANSACTION_TYPE_DISPLAY.SALE.variant,
    status: { requested: ORDER_DISPLAY_COPY.customerRequestReceived, ordered: ORDER_DISPLAY_COPY.saleConfirmed },
    dates: { submittedAt: ORDER_DISPLAY_COPY.requestReceivedOn, orderedAt: ORDER_DISPLAY_COPY.saleConfirmedOn, arrivedDate: ORDER_DISPLAY_COPY.dateShipped },
    requestor: ORDER_DISPLAY_COPY.customerRequestor,
    placeAction: ORDER_DISPLAY_COPY.initiateSale,
    fulfillAction: ORDER_DISPLAY_COPY.markFulfilledShipped,
    movement: {
      plural: "Shipments", singular: "Shipment", past: "Sent",
      menu: ORDER_DISPLAY_COPY.outgoingShipments, total: ORDER_DISPLAY_COPY.totalShipped, externalBatchQualifier: "External",
    },
  },
} as const satisfies Record<OrderDirection, DirectionDisplay>;

function directionLabelEntry(direction: OrderDirection): readonly [OrderDirection, string] {
  return [direction, ORDER_DIRECTION_DISPLAY[direction].label];
}

export const ORDER_DIRECTION_LABELS = Object.fromEntries(
  ORDER_DIRECTION_VALUES.map(directionLabelEntry),
) as Record<OrderDirection, string>;

const ORDER_STATUS_VARIANTS: Record<OrderStatus, BadgeVariant> = {
  requested: "outline",
  ordered: "secondary",
  fulfilled: "default",
  cancelled: "destructive",
};

export function orderStatusLabel(status: OrderStatus, balanceDue?: DecimalValue | null): string {
  return isFulfilledWithBalanceDue({ status, balanceDue }) ? FULFILLED_BALANCE_DUE_LABEL : ORDER_STATUS_LABELS[status];
}

export function orderLifecycleStatusLabel(
  status: OrderStatus,
  direction: OrderDirection,
  balanceDue?: DecimalValue | null,
): string {
  if (status === ORDER_STATUSES.requested || status === ORDER_STATUSES.ordered) {
    return ORDER_DIRECTION_DISPLAY[direction].status[status];
  }
  return orderStatusLabel(status, balanceDue);
}

export function orderStatusVariant(status: OrderStatus, balanceDue?: DecimalValue | null): BadgeVariant {
  return isFulfilledWithBalanceDue({ status, balanceDue }) ? "destructive" : ORDER_STATUS_VARIANTS[status];
}

export const ORDER_HISTORY_EVENT_LABELS: Record<OrderHistoryEventType, string> = {
  imported: "Imported",
  requested: "Requested",
  placed: "Placed",
  fulfilled: "Fulfilled",
  cancelled: "Cancelled",
  reopened: "Reopened",
  payment_recorded: ORDER_DISPLAY_COPY.paymentRecorded,
  payment_amended: ORDER_DISPLAY_COPY.paymentAmended,
  payment_deleted: ORDER_DISPLAY_COPY.paymentDeleted,
  invoice_recorded: ORDER_DISPLAY_COPY.invoiceRecorded,
  invoice_amended: ORDER_DISPLAY_COPY.invoiceAmended,
  invoice_deleted: ORDER_DISPLAY_COPY.invoiceDeleted,
  receipt_recorded: ORDER_DISPLAY_COPY.receiptRecorded,
  shipment_recorded: ORDER_DISPLAY_COPY.shipmentRecorded,
  movement_amended: ORDER_DISPLAY_COPY.receiptShipmentAmended,
  movement_deleted: ORDER_DISPLAY_COPY.receiptShipmentDeleted,
  document_uploaded: ORDER_DISPLAY_COPY.documentUploaded,
  document_replaced: ORDER_DISPLAY_COPY.documentReplaced,
  document_deleted: ORDER_DISPLAY_COPY.documentDeleted,
  amended: "Amended",
  returned: "Returned",
  credit_memo_recorded: ORDER_DISPLAY_COPY.creditMemoRecorded,
  credit_memo_deleted: ORDER_DISPLAY_COPY.creditMemoDeleted,
  document_previewed: ORDER_DISPLAY_COPY.documentPreviewed,
  document_mark_applied: ORDER_DISPLAY_COPY.documentMarkApplied,
  document_generated: ORDER_DISPLAY_COPY.documentGenerated,
  document_corrected: ORDER_DISPLAY_COPY.documentCorrected,
  document_viewed: ORDER_DISPLAY_COPY.documentViewed,
  document_download_requested: ORDER_DISPLAY_COPY.documentDownloadRequested,
};

const ORDER_HISTORY_EVENT_VARIANTS: Partial<Record<OrderHistoryEventType, BadgeVariant>> = {
  requested: "outline",
  placed: "secondary",
  fulfilled: "default",
  cancelled: "destructive",
  reopened: "secondary",
  payment_recorded: "default",
  payment_amended: "secondary",
  payment_deleted: "destructive",
  invoice_recorded: "default",
  invoice_amended: "secondary",
  invoice_deleted: "destructive",
  receipt_recorded: "default",
  shipment_recorded: "default",
  movement_amended: "secondary",
  movement_deleted: "destructive",
  document_uploaded: "default",
  document_replaced: "secondary",
  document_deleted: "destructive",
  amended: "secondary",
  returned: "destructive",
  credit_memo_recorded: "default",
  credit_memo_deleted: "destructive",
  document_generated: "default",
  document_corrected: "secondary",
};

export function orderHistoryEventLabel(eventType: string): string {
  return isOrderHistoryEventType(eventType) ? ORDER_HISTORY_EVENT_LABELS[eventType] : eventType;
}

export function orderHistoryEventVariant(eventType: string): BadgeVariant {
  return (isOrderHistoryEventType(eventType) ? ORDER_HISTORY_EVENT_VARIANTS[eventType] : undefined) ?? "outline";
}

export const ORDER_STATUS_CHAIN: readonly OrderStatus[] = [ORDER_STATUSES.requested, ORDER_STATUSES.ordered, ORDER_STATUSES.fulfilled];

export function orderDirectionVariant(direction: OrderDirection): BadgeVariant {
  return ORDER_DIRECTION_DISPLAY[direction].variant;
}

export function orderedAtLabel(direction: OrderDirection): string {
  return ORDER_DIRECTION_DISPLAY[direction].dates.orderedAt;
}

export function submittedAtLabel(direction: OrderDirection): string {
  return ORDER_DIRECTION_DISPLAY[direction].dates.submittedAt;
}

export function recordedAtLabel(): string {
  return ORDER_DISPLAY_COPY.recordedAt;
}

export function requestorLabel(direction: OrderDirection): string {
  return ORDER_DIRECTION_DISPLAY[direction].requestor;
}

export function arrivedDateLabel(direction: OrderDirection): string {
  return ORDER_DIRECTION_DISPLAY[direction].dates.arrivedDate;
}

export const PRICE_PER_UNIT_LABEL = ORDER_DISPLAY_COPY.pricePerUnitUnit;

export const ORDER_PENDING_ACTIONS = { place: ORDER_ACTIONS.place, cancel: ORDER_ACTIONS.cancel, return: "return" } as const;
export type OrderActionKey = typeof ORDER_PENDING_ACTIONS.place | typeof ORDER_PENDING_ACTIONS.cancel;
export type OrderPendingAction = typeof ORDER_PENDING_ACTIONS[keyof typeof ORDER_PENDING_ACTIONS];

export type OrderAction = {
  key: OrderActionKey;
  label: string;
  disabled?: boolean;
  variant?: "outline" | "default";
};

export function placeActionLabel(direction: OrderDirection): string {
  return ORDER_DIRECTION_DISPLAY[direction].placeAction;
}

export function fulfillActionLabel(direction: OrderDirection): string {
  return ORDER_DIRECTION_DISPLAY[direction].fulfillAction;
}

export function getOrderActions(status: OrderStatus, direction: OrderDirection): OrderAction[] {
  if (status === ORDER_STATUSES.fulfilled || status === ORDER_STATUSES.cancelled) return [];

  const actions: OrderAction[] = [{ key: ORDER_PENDING_ACTIONS.cancel, label: ORDERS_DIALOG_STACK_COPY.cancelOrder, variant: "outline" }];

  if (status === ORDER_STATUSES.requested) {
    actions.push({ key: ORDER_PENDING_ACTIONS.place, label: placeActionLabel(direction) });
  }

  return actions;
}

export type OrderCoreViewFields = {
  id: number;
  chemicalName: string;
  casNumber: string;
  contactName: string;
  contactId: number;
  direction: OrderDirection;
  quantity: DecimalValue;
  unit: string;
  pricePerItem: DecimalValue | null;
  internalProductNumber: string | null;
  externalProductNumber: string | null;
  referenceNumber: string | null;
  customerPoNumber: string | null;
  contactAddressId: number | null;
  internalBatchNumber: string | null;
  externalBatchNumber: string | null;
  cancellationReason: string | null;
  status: OrderStatus;
  submittedBy: string;
  submittedAt: string;
  recordedAt: string;
  requestedBy: string;
  orderedBy: string | null;
  orderedAt: string | null;
  deliverySchedule: DeliveryEntry[];
  movedQuantity: DecimalValue;
  arrivedDate: string | null;
  notes: string | null;
  hasDocuments?: boolean;
  persistedPaidTotal?: DecimalValue;
  returnedAt: string | null;
  returnNotes: string | null;
};

export type OrderLocalOverlayFields = {
  payments: OrderPayment[];
  orderHistory: OrderHistoryEntry[];
  creditMemos: OrderCreditMemo[];
};

export type OrderViewModel = OrderCoreViewFields & OrderLocalOverlayFields;

export type OrderPayment = {
  id: number;
  amount: DecimalValue;
  paidDate: string;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  notes: string | null;
  recordedBy: string;
  recordedAt: string;
};

export type OrderCreditMemo = {
  id: number;
  amount: DecimalValue;
  date: string;
  referenceNumber: string | null;
  notes: string | null;
  recordedBy: string;
  recordedAt: string;
};

export type OrderHistoryField = { label: string; value: string };

export type OrderHistoryEntry = {
  id: number;
  eventType: OrderHistoryEventType;
  userName: string;
  eventDate: string;
  fields?: OrderHistoryField[];
  recordedAt?: string | null;
};

export { toNoonUtcIso } from "@shared/values/date-only";

export function orderPriceInTotal(order: Pick<OrderViewModel, "pricePerItem" | "quantity">): DecimalValue | null {
  return orderValue(order.pricePerItem, order.quantity);
}

export function orderTotalPaid(order: Pick<OrderViewModel, "payments" | "persistedPaidTotal">): DecimalValue {
  return order.persistedPaidTotal ?? decimalSum(order.payments.map((payment) => payment.amount));
}

export function orderBalanceDue(order: Pick<OrderViewModel, "pricePerItem" | "quantity" | "payments" | "persistedPaidTotal">): DecimalValue | null {
  return orderBalance(order.pricePerItem, order.quantity, orderTotalPaid(order));
}

const IMPORT_RECORDING_WINDOW_MS = 2 * 60 * 1000;

function wroteByImport(recordedAt: string, importedAt: string | null | undefined): boolean {
  return Boolean(importedAt) && Math.abs(new Date(recordedAt).getTime() - new Date(importedAt!).getTime()) <= IMPORT_RECORDING_WINDOW_MS;
}

export function recordedTimeToShow(
  entry: Pick<OrderHistoryEntry, "eventType" | "eventDate" | "recordedAt">,
  importedAt?: string | null,
): string | null {
  if (!entry.recordedAt || entry.eventType === ORDER_HISTORY_EVENTS.imported) return null;
  if (wroteByImport(entry.recordedAt, importedAt)) return null;
  if (easternCalendarDay(entry.recordedAt) === easternCalendarDay(entry.eventDate)) return null;
  return formatDateTimeEastern(entry.recordedAt);
}
